import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Form, Input, Button, Radio, Select, Tooltip, Icon } from 'antd'
import AddAndEditPhoneNumberModal from './AddAndEditPhoneNumberModal'
import css from './phone-number-purchase-form.css'

const { Option } = Select

const isNotAustralia = country => country !== 'AU'
const isNotNewZealand = country => country !== 'NZ'
const isNotItaly = country => country !== 'IT'
const checkAreaCode = country => ['US', 'CA'].some(code => code === country)
const hasBusiness = selectedBusinessId => selectedBusinessId !== null

export const shouldDisableButton = (country, areaCode, selectedBusinessId) =>
  isNotAustralia(country) &&
  isNotNewZealand(country) &&
  isNotItaly(country) &&
  (!hasBusiness(selectedBusinessId) ||
  !((areaCode && areaCode.match(/^\d{3}$/)) || !country))

class PhoneNumberPurchaseForm extends Component {
  static propTypes = {
    businesses: PropTypes.arrayOf(PropTypes.object).isRequired,
    buyPhoneNumbers: PropTypes.func.isRequired,
    isTopsAdmin: PropTypes.bool.isRequired,
    locationId: PropTypes.string.isRequired,
    locations: PropTypes.arrayOf(PropTypes.object).isRequired,
    phoneNumberModalAction: PropTypes.string,
    phoneNumberModalOpen: PropTypes.bool.isRequired,
    refetchLocations: PropTypes.func.isRequired,
    toggleModal: PropTypes.func.isRequired,
    handleSelectedBusiness: PropTypes.func.isRequired,
    selectedBusinessId: PropTypes.number,
  }

  static defaultProps = {
    phoneNumberModalAction: 'edit',
    selectedBusinessId: null,
  }

  state = {
    country: 'US',
    areaCode: null,
    loading: false,
    addressSID: null,
    bundleSID: null,
  }

  handleAreaCodeChange = e => {
    this.setState({
      areaCode: e.target.value,
    })
  }

  handleAddressSIDChange = e => {
    this.setState({
      addressSID: e.target.value,
    })
  }

  handleBundleSIDChange = e => {
    this.setState({
      bundleSID: e.target.value,
    })
  }

  onCountryChange = e => {
    this.setState({
      country: e.target.value,
    })
  }

  handleBuyNumbers = () => {
    this.setState({ loading: true })
    this.props.buyPhoneNumbers(
      this.state.country,
      this.state.areaCode,
      this.props.locationId,
      this.state.addressSID,
      this.state.bundleSID,
    )
  }

  render() {
    const { country, areaCode, addressSID, bundleSID, loading } = this.state
    const {
      businesses,
      countryCode,
      isTopsAdmin,
      locationId,
      locations,
      phoneNumberModalAction,
      phoneNumberModalOpen,
      refetchLocations,
      toggleModal,
      selectedBusinessId,
      handleSelectedBusiness,
    } = this.props
    const disableButton = shouldDisableButton(country, areaCode, selectedBusinessId)
    const needsAreaCode = checkAreaCode(country)
    const needsSids = country === 'AU' || country === 'IT'
    const purchaseButtonText =
      country === 'NZ' ? 'Purchase SMS Account' : 'Buy Phone Numbers'
    const showManualPhoneNumberEntry =
      !isTopsAdmin && countryCode !== 'NZ' && country !== 'NZ'

    return (
      <Form className={css.wrapper}>
        <Form.Item label="Country" className={css.countryCodeRadio}>
          <Radio.Group
            name="pmsType"
            onChange={this.onCountryChange}
            value={country}
            size="small"
          >
            <Radio.Button value="US">US</Radio.Button>
            <Radio.Button value="CA">CA</Radio.Button>
            <Radio.Button value="AU">AU</Radio.Button>
            <Radio.Button value="NZ">NZ</Radio.Button>
            <Radio.Button value="IT">IT</Radio.Button>
          </Radio.Group>
        </Form.Item>
        {needsAreaCode && (
          <Form.Item label="Area Code" className={css.areaCodeInput}>
            <Input
              placeholder="e.g. 415"
              size="small"
              addonBefore="+1"
              onChange={this.handleAreaCodeChange}
              value={areaCode}
            />
          </Form.Item>
        )}
        {needsSids && (
          <Form.Item label="Address SID" className={css.sidInput}>
            <Input
              placeholder="e.g. ADXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
              size="small"
              onChange={this.handleAddressSIDChange}
              value={addressSID}
            />
          </Form.Item>
        )}
        {needsSids && (
          <Form.Item label="Bundle SID" className={css.sidInput}>
            <Input
              placeholder="e.g. BUXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
              size="small"
              onChange={this.handleBundleSIDChange}
              value={bundleSID}
            />
          </Form.Item>
        )}
        <Form.Item>
          <div className="ant-checkbox-modifier">
            <style>
              {`.ant-checkbox-modifier .ant-checkbox-wrapper {
                margin-left: 0;
              }`}
            </style>
            <h4>
              Business&nbsp;
              <Tooltip
                key="business-tooltip"
                title={
                  <span>
                    The business is used for Twilio compliance. If the business
                    doesn&apos;t have an EIN, all messages will be appended with
                    the attribution &quot;-&nbsp;messaging by RoosterGrin&quot; (or Tops).
                    <br />
                    <br />
                    Note that we also have to attribute messages during the
                    Twilio approval process, which takes a few days.
                  </span>
                }
                placement="top"
                arrowPointAtCenter
              >
                <Icon type="question-circle" theme="outlined" />
              </Tooltip>
            </h4>
            <div className={css.locationCheckboxes}>
              <Select
                name="business"
                onChange={handleSelectedBusiness}
                value={selectedBusinessId}
              >
                <Option key="no-business-selected" value={null}>
                  None Selected
                </Option>
                {businesses.length &&
                  businesses.map(({ id, name }) => (
                    <Option key={id} value={id}>
                      {name}
                    </Option>
                  ))}
              </Select>
            </div>
          </div>
        </Form.Item>
        <Form.Item label="" className={css.buyNumbersButton}>
          <Button
            disabled={disableButton}
            onClick={this.handleBuyNumbers}
            loading={loading}
          >
            {purchaseButtonText}
          </Button>
        </Form.Item>
        {showManualPhoneNumberEntry && (
          <div className={css.manualEntryButton}>
            <Button onClick={() => toggleModal(true, 'add')}>
              Manually Add Phone Number
            </Button>
            <AddAndEditPhoneNumberModal
              action={phoneNumberModalAction}
              businesses={businesses}
              handleSelectedBusiness={handleSelectedBusiness}
              selectedBusinessId={selectedBusinessId}
              countryCode={countryCode}
              locationId={locationId}
              locations={locations}
              open={phoneNumberModalOpen}
              purchasedNumbers={[]}
              refetchLocations={refetchLocations}
              toggleModal={toggleModal}
            />
          </div>
        )}
      </Form>
    )
  }
}

export default PhoneNumberPurchaseForm



// WEBPACK FOOTER //
// ./src/AddPractice/Associations/PhoneNumberPurchaseForm.js